import React, { useEffect, useMemo, useState } from "react";
import { OrbitProgress } from "react-loading-indicators";

import { useScheduledSettingsTrigger } from "../../services/API/ControllerAPI/GetUpdatedSettings.jsx";

import {
  FileDown,
  RefreshCw,
  Store,
  Activity,
  ListChecks,
  CalendarDays,
  AlertTriangle,
} from "lucide-react";
import LogDisplay from "../common/LogDisplay";
import { UseShopLogsData } from "../../services/API/ShopAPI/GetShopLogs";
import { useAllShopsData } from "../../services/API/ShopAPI/GetAllShopDetails";
import { UseShopControllersData } from "../../services/API/ShopAPI/GetShopControllers";
import ShopLogsChart from "../../components/charts/ShopLogsChart";
import SimpleShopDetails from "../common/TableComponents/SimpleShopDetails";
import SimpleDeviceSettigns from "../common/TableComponents/SimpleDeviceSettigns";
import SimplePDFStyle from "../common/PDF/SimplePDFStyle";
import PdfPreview from "../common/PDF/PdfPreview.jsx";
import MonthlyReportPdfPreview from "../common/PDF/MonthlyReportPdfPreview.jsx";
import { useMonthlyReportData } from "../../services/API/ShopAPI/GetMonthlyReport";
import { GET_CONTROLLER_STATUS } from "../constants.js";

/**
 * Reusable dashboard metric card.
 * This component is responsible only for the UI.
 * It does not contain any business logic.
 */
const MetricCard = ({
  title,
  value,
  icon: Icon,
  iconBg = "bg-stone-100",
  iconColor = "text-stone-700",
  valueColor = "text-stone-900",
}) => {
  return (
    <div className="h-full min-w-0 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md">
      <div className="flex h-full items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-stone-500">{title}</p>

          <h2 className={`mt-3 text-4xl font-bold ${valueColor}`}>{value}</h2>
        </div>

        {Icon && (
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconBg}`}
          >
            <Icon className={`h-6 w-6 ${iconColor}`} />
          </div>
        )}
      </div>
    </div>
  );
};

const ShopLogs = ({ initialShop }) => {
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [emailSearch, setEmailSearch] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);

  /*
   * Controls whether the PDF preview modal is visible.
   */
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showMonthlyReportPreview, setShowMonthlyReportPreview] =
    useState(false);

  // Stores the device IDs that failed during the latest controller refresh.
  const [failedControllerIds, setFailedControllerIds] = useState([]);

  // Refreshes controller settings and returns each controller refresh status.
  const refreshControllerSettingsMutation = useScheduledSettingsTrigger();

  // Controls whether the monthly report is being generated.
  const [isMonthlyReportLoading, setIsMonthlyReportLoading] = useState(false);

  // Store the monthly report returned by the API.
  const [monthlyReportData, setMonthlyReportData] = useState(null);

  // Controls whether the page header is visible.
  const [showHeader, setShowHeader] = useState(true);

  // Current date used by the dashboard.
  const currentDate = new Date();

  // Store the year used to request the monthly report.
  const [reportYear, setReportYear] = useState(currentDate.getFullYear());

  // Store the month used to request the monthly report.
  const [reportMonth, setReportMonth] = useState(currentDate.getMonth() + 1);

  const { refetch: refetchMonthlyReport } = useMonthlyReportData(
    selectedShop,
    reportYear,
    reportMonth,
  );

  useEffect(() => {
    // Clear the current selection when no initial shop is provided.
    if (!initialShop) {
      setSelectedBrand("");
      setSelectedShop("");
      setEmailSearch("");
      return;
    }

    // Apply the initial shop selection when one is provided.
    setSelectedBrand(initialShop.brand ?? "");
    setSelectedShop(String(initialShop.id));
  }, [initialShop]);

  const {
    data: allShopsData = [],
    isPending: isAllShopsDataPending,
    error: allShopsDataError,
  } = useAllShopsData();

  // Hide test shops from the dashboard.
  const visibleShops = useMemo(() => {
    return allShopsData.filter((shop) => shop.is_shop_test !== true);
  }, [allShopsData]);

  // Find the currently selected shop details.
  const selectedShopData = useMemo(
    () => allShopsData.find((shop) => String(shop.id) === String(selectedShop)),
    [allShopsData, selectedShop],
  );

  // Build the sorted list of available brands.
  const brandOptions = useMemo(
    () =>
      [
        ...new Set(visibleShops.map((shop) => shop.brand).filter(Boolean)),
      ].sort(),
    [visibleShops],
  );

  // Build the list of shops for the selected brand.
  const shopOptions = useMemo(() => {
    if (!selectedBrand) {
      return [];
    }

    return visibleShops
      .filter((shop) => shop.brand === selectedBrand)
      .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
  }, [visibleShops, selectedBrand]);

  // Build the list of shops that match the entered customer email.
  const emailShopResults = useMemo(() => {
    const normalizedEmail = emailSearch.trim().toLowerCase();

    // Do not display results until the user starts typing.
    if (!normalizedEmail) {
      return [];
    }

    // Return every shop created with the matching customer email.
    return allShopsData
      .filter((shop) => {
        const shopEmail = String(shop.user_email ?? "").toLowerCase();

        return shopEmail.includes(normalizedEmail);
      })
      .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
  }, [allShopsData, emailSearch]);

  /*
   * Load the activity logs for the selected shop.
   * This request runs only when a shop is selected.
   */
  const {
    data: shopLogs,
    isPending: isShopLogsPending,
    isFetching: isShopLogsFetching,
    error: shopLogsError,
    refetch: refetchShopLogs,
  } = UseShopLogsData(selectedShop);

  /*
   * Load all controllers that belong to the selected shop.
   * This request runs only when a shop is selected.
   */
  const { data: shopControllerData, refetch: refetchShopControllers } =
    UseShopControllersData(selectedShop, {
      enabled: !!selectedShop,
    });

  /*
   * Extract the controllers array from the API response.
   * If the response is empty, return an empty array.
   */
  const shopControllers = useMemo(
    () =>
      Array.isArray(shopControllerData?.controllerDetails)
        ? shopControllerData.controllerDetails
        : [],
    [shopControllerData],
  );

  // Gets all unique controller IDs from the selected shop.
  const shopControllerIds = useMemo(() => {
    return [
      ...new Set(
        shopControllers
          .map((controller) => controller?.id ?? controller?.device_id)
          .filter(Boolean),
      ),
    ];
  }, [shopControllers]);

  // Refreshes controller status whenever the selected shop controllers change.
  useEffect(() => {
    // Stops when there are no controllers to refresh.
    if (shopControllerIds.length === 0) {
      setFailedControllerIds([]);
      return;
    }

    let isCancelled = false;

    const refreshControllerStatus = async () => {
      try {
        const refreshResult =
          await refreshControllerSettingsMutation.mutateAsync({
            controllerIds: shopControllerIds,
          });

        if (isCancelled) {
          return;
        }

        // Stores the controller IDs that failed during the refresh.
        const failedIds =
          refreshResult.details
            ?.filter((item) => item?.status === "error")
            .map((item) => item?.deviceId)
            .filter(Boolean) ?? [];

        setFailedControllerIds(failedIds);
      } catch (error) {
        console.error("Unable to refresh controller status:", error);
      }
    };

    refreshControllerStatus();

    return () => {
      isCancelled = true;
    };
  }, [shopControllerIds]);

  // /*
  //  * Count how many controllers are currently online.
  //  * The shared controller status helper keeps this logic
  //  * consistent with the PDF preview.
  //  */
  // const controllersOnline = useMemo(() => {
  //   return shopControllers.filter(
  //     (controller) => GET_CONTROLLER_STATUS(controller) === "Online",
  //   ).length;
  // }, [shopControllers]);

  // Counts controllers that did not fail during the latest refresh.
  const controllersOnline = useMemo(() => {
    return shopControllers.filter((controller) => {
      const controllerId = String(controller?.id ?? controller?.device_id ?? "")
        .trim()
        .toLowerCase();

      const hasRefreshError = failedControllerIds.some(
        (deviceId) =>
          String(deviceId ?? "")
            .trim()
            .toLowerCase() === controllerId,
      );

      return !hasRefreshError;
    }).length;
  }, [shopControllers, failedControllerIds]);

  /**
   * Calculates this month's transaction totals.
   */
  const validTransactionsThisMonth = useMemo(() => {
    return (shopLogs?.validKeyTransactions ?? []).filter((transaction) => {
      const transactionDate = new Date(transaction.timestamp);

      return (
        transactionDate.getMonth() === currentDate.getMonth() &&
        transactionDate.getFullYear() === currentDate.getFullYear()
      );
    }).length;
  }, [shopLogs]);

  const invalidTransactionsThisMonth = useMemo(() => {
    return (shopLogs?.invalidKeyTransactions ?? []).filter((transaction) => {
      const transactionDate = new Date(transaction.timestamp);

      return (
        transactionDate.getMonth() === currentDate.getMonth() &&
        transactionDate.getFullYear() === currentDate.getFullYear()
      );
    }).length;
  }, [shopLogs]);

  /**
   * Loads the monthly report from the backend
   * and opens the monthly report preview.
   */
  const handleOpenMonthlyReport = async () => {
    // Do not request the report if no shop is selected.
    if (!selectedShop) {
      return;
    }

    setIsMonthlyReportLoading(true);

    try {
      // Request the monthly report using the selected shop and period.
      const monthlyReportResult = await refetchMonthlyReport();

      // Stop if the API did not return report data.
      if (!monthlyReportResult?.data) {
        throw new Error("The monthly report API returned no data.");
      }

      // Store the report returned by the backend.
      setMonthlyReportData(monthlyReportResult.data);

      // Open the preview after the report has been loaded.
      setShowMonthlyReportPreview(true);
    } catch (error) {
      console.error("Unable to load monthly report:", error);
    } finally {
      setIsMonthlyReportLoading(false);
    }
  };

  useEffect(() => {
    // Do not reload if the monthly report preview is closed.
    if (!showMonthlyReportPreview) {
      return;
    }

    // Reload the report when the selected period changes.
    const reloadMonthlyReport = async () => {
      try {
        setIsMonthlyReportLoading(true);

        const monthlyReportResult = await refetchMonthlyReport();

        if (!monthlyReportResult?.data) {
          throw new Error("The monthly report API returned no data.");
        }

        // Store the report returned for the selected period.
        setMonthlyReportData(monthlyReportResult.data);
      } catch (error) {
        console.error("Unable to reload monthly report:", error);
      } finally {
        setIsMonthlyReportLoading(false);
      }
    };

    reloadMonthlyReport();
  }, [reportYear, reportMonth, showMonthlyReportPreview, refetchMonthlyReport]);

  /**
   * Refreshes the selected shop data and generates the PDF
using the latest information returned by the backend.
   */
  const handleExportPDF = async () => {
    // Do not generate a report if no shop is selected.
    if (!selectedShopData) {
      return;
    }

    try {
      const [refreshedLogsResult, refreshedControllersResult] =
        await Promise.all([refetchShopLogs(), refetchShopControllers()]);

      /*
       * Use the refreshed data returned directly by React Query.
       * Fall back to the current data if no new payload is returned.
       */
      const latestShopLogs = refreshedLogsResult?.data ?? shopLogs;

      const latestControllerResponse =
        refreshedControllersResult?.data ?? shopControllerData;

      const latestControllers = Array.isArray(
        latestControllerResponse?.controllerDetails,
      )
        ? latestControllerResponse.controllerDetails
        : shopControllers;

      /*
       * Generate the PDF using the latest available information.
       */
      await SimplePDFStyle({
        shop: selectedShopData,
        shopLogs: latestShopLogs,
        controllers: latestControllers,
        selectedPeriod: "All available data",
      });

      /*
       * Close the preview after the PDF has been generated.
       */
      setShowPdfPreview(false);
    } catch (error) {
      console.error("Unable to export PDF:", error);
    }
  };

  useEffect(() => {
    const scrollContainer = document.getElementById("shop-logs-content");

    // Stop if the scrollable content was not found.
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      // Show the header only when the content is near the top.
      setShowHeader(scrollContainer.scrollTop < 50);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (isAllShopsDataPending) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <OrbitProgress color="#444444" size="medium" />
      </div>
    );
  }

  // Display an error message when the shop list cannot be loaded.
  if (allShopsDataError) {
    return (
      <div className="p-6 text-red-600">
        Error loading shops: {allShopsDataError?.message}
      </div>
    );
  }

  /**
   * Refresh the shop logs and controller data.
   */
  const handleRefreshLogs = async () => {
    try {
      // Refresh both requests in parallel.
      await Promise.all([refetchShopLogs(), refetchShopControllers()]);
    } catch (error) {
      console.error("Unable to refresh shop logs:", error);
    }
  };

  return (
    <>
      <div className="p-4 bg-stone-100 h-full overflow-hidden">
        <div className="flex h-[calc(100vh-180px)] flex-col rounded-lg border border-stone-200">
          {/* Responsive shop logs header */}
          <div className="flex flex-col gap-4 rounded-t-lg border-b border-stone-200 bg-stone-50 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
            <div className="w-full min-w-0 md:w-auto md:min-w-[360px]">
              <h1 className="flex items-center gap-2 text-lg font-bold text-stone-900">
                <Store size={20} />
                Shop Logs
              </h1>

              {showHeader && (
                <p className="mt-1 text-sm text-stone-500">
                  Select a brand and shop to view activity logs.
                </p>
              )}
            </div>

            {/* Filters and actions */}

            {showHeader && (
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:flex-1 xl:items-end xl:justify-end">
                {/* Search by email */}

                <div className="relative w-full max-w-[300px]">
                  <label className="mb-1 block text-xs font-medium text-stone-600">
                    Search by customer email
                  </label>

                  <input
                    type="email"
                    value={emailSearch}
                    onChange={(event) => {
                      setEmailSearch(event.target.value);
                    }}
                    placeholder="customer@email.com"
                    className="h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
                  />

                  {emailSearch.trim() && (
                    <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-64 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
                      {emailShopResults.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-stone-500">
                          No shops found for this email.
                        </div>
                      ) : (
                        emailShopResults.map((shop) => (
                          <button
                            key={shop.id}
                            type="button"
                            onClick={() => {
                              // Reuse the existing brand and shop selection flow.
                              setSelectedBrand(shop.brand ?? "");
                              setSelectedShop(String(shop.id));

                              // Close the search result list after selecting a shop.
                              setEmailSearch("");
                            }}
                            className="flex w-full flex-col border-b border-stone-100 px-4 py-3 text-left hover:bg-stone-50 last:border-b-0"
                          >
                            <span className="text-sm font-semibold text-stone-900">
                              {shop.name || "Unnamed shop"}
                            </span>

                            <span className="text-xs text-stone-500">
                              {shop.brand || "No brand"}
                            </span>

                            <span className="text-xs text-stone-400">
                              {shop.user_email || "No email"}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Brand */}

                <div className="w-full xl:w-56">
                  <label className="mb-1 block text-xs font-medium text-stone-600">
                    Brand
                  </label>

                  <select
                    value={selectedBrand}
                    onChange={(event) => {
                      // Clear the current shop selection and email search.
                      setSelectedBrand(event.target.value);
                      setSelectedShop("");
                      setEmailSearch("");
                    }}
                    className="h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
                  >
                    <option value="">All Brands</option>

                    {brandOptions.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Shop */}

                <div className="w-full xl:w-56">
                  <label className="mb-1 block text-xs font-medium text-stone-600">
                    Shop
                  </label>

                  <select
                    value={selectedShop}
                    onChange={(event) => setSelectedShop(event.target.value)}
                    disabled={!selectedBrand}
                    className="h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-700 disabled:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-stone-300"
                  >
                    <option value="">
                      {selectedBrand ? "Select Shop" : "Select Brand First"}
                    </option>

                    {shopOptions.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Export report */}

                <div className="w-full sm:col-span-2 xl:col-span-1 xl:w-auto xl:min-w-[520px]">
                  <label className="mb-1 block text-xs font-medium text-stone-600">
                    Shop Activity Report
                  </label>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <button
                      type="button"
                      onClick={() => setShowPdfPreview(true)}
                      disabled={
                        !selectedBrand ||
                        !selectedShop ||
                        isShopLogsPending ||
                        !shopLogs
                      }
                      className="flex h-10 flex-1 items-center cursor-pointer justify-center gap-2 rounded-lg border border-stone-300 bg-white px-4 text-sm font-bold text-stone-700 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FileDown size={18} />
                      Export PDF
                    </button>

                    <button
                      type="button"
                      onClick={handleOpenMonthlyReport}
                      disabled={
                        !selectedBrand ||
                        !selectedShop ||
                        isShopLogsPending ||
                        !shopLogs ||
                        isMonthlyReportLoading
                      }
                      className="flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#E96B78] bg-[#E96B78] px-4 text-sm font-bold text-white transition-colors hover:bg-[#d95b69] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isMonthlyReportLoading ? (
                        <RefreshCw size={18} className="animate-spin" />
                      ) : (
                        <FileDown size={18} />
                      )}
                      Monthly Report
                    </button>

                    <button
                      type="button"
                      onClick={handleRefreshLogs}
                      disabled={
                        !selectedBrand || !selectedShop || isShopLogsFetching
                      }
                      className="flex h-10 flex-1 items-center cursor-pointer justify-center gap-2 rounded-lg border border-stone-300 bg-white px-4 text-sm font-bold text-stone-700 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw
                        size={18}
                        className={isShopLogsFetching ? "animate-spin" : ""}
                      />
                      Refresh Logs
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            id="shop-logs-content"
            onScroll={(event) => {
              // Show the header only when the content is near the top.
              setShowHeader(event.currentTarget.scrollTop < 50);
            }}
            className="flex-1 overflow-x-auto overflow-y-auto bg-white p-5"
          >
            {/* Display a placeholder until a shop is selected. */}
            {!selectedShop && (
              <div className="h-full flex items-center justify-center text-sm text-stone-500">
                Select a brand and shop to load logs.
              </div>
            )}

            {/* Display a loading indicator while the shop logs are being fetched. */}
            {selectedShop && isShopLogsPending && (
              <div className="p-6 flex justify-center items-center h-64">
                <OrbitProgress color="#444444" size="medium" />
              </div>
            )}

            {/* Display an error message when the shop logs cannot be loaded. */}
            {selectedShop && shopLogsError && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 font-medium">
                Error loading shop logs.
              </div>
            )}

            {/* Display the shop dashboard after the logs have been loaded. */}
            {selectedShop && shopLogs && (
              <div className="space-y-6">
                {/* Display the shop summary metrics and security chart. */}
                <div className="space-y-6">
                  {/* Responsive dashboard metrics */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                    {/* Controllers online metric */}
                    <MetricCard
                      title="Controllers Online"
                      value={controllersOnline}
                      icon={Activity}
                      iconBg="bg-stone-100"
                      iconColor="text-stone-800"
                      valueColor="text-stone-500"
                    />
                    {/* Total valid transactions metric */}
                    <MetricCard
                      title="Total Valid Transactions"
                      value={shopLogs?.validKeyTransactions?.length ?? 0}
                      icon={ListChecks}
                      iconBg="bg-stone-100"
                      iconColor="text-stone-800"
                      valueColor="text-green-700"
                    />

                    {/* Total invalid transactions metric */}
                    <MetricCard
                      title="Total Invalid Transactions"
                      value={shopLogs?.invalidKeyTransactions?.length ?? 0}
                      icon={AlertTriangle}
                      iconBg="bg-stone-100"
                      iconColor="text-stone-800"
                      valueColor="text-red-700"
                    />

                    {/* Valid transactions this month */}
                    <MetricCard
                      title="Valid Transactions This Month"
                      value={validTransactionsThisMonth}
                      icon={CalendarDays}
                      iconBg="bg-stone-100"
                      iconColor="text-stone-800"
                      valueColor="text-green-700"
                    />

                    {/* Invalid transactions this month */}
                    <MetricCard
                      title="Invalid Transactions This Month"
                      value={invalidTransactionsThisMonth}
                      icon={CalendarDays}
                      iconBg="bg-stone-100"
                      iconColor="text-stone-800"
                      valueColor="text-red-700"
                    />
                  </div>

                  {/* Device action buttons */}
                  {/* Security events section */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <div className="flex flex-col gap-3">
                      {/* Shop Information */}
                      <div className="border-b border-stone-200 pb-3">
                        <h3 className="text-base font-bold text-stone-900">
                          Shop Information
                        </h3>
                      </div>
                      {/* Brand */}
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-stone-500">
                            Brand:
                          </span>{" "}
                          <span className="font-semibold text-stone-800">
                            {selectedShopData?.brand || "—"}
                          </span>
                        </div>
                        {/* Shop Name */}
                        <div>
                          <span className="font-medium text-stone-500">
                            Shop:
                          </span>{" "}
                          <span className="font-semibold text-stone-800">
                            {selectedShopData?.name || "—"}
                          </span>
                        </div>
                        {/* Email */}
                        <div>
                          <span className="font-medium text-stone-500">
                            Email:
                          </span>{" "}
                          <span className="break-all font-semibold text-stone-800">
                            {selectedShopData?.user_email || "—"}
                          </span>
                        </div>
                        {/* Country */}
                        <div>
                          <span className="font-medium text-stone-500">
                            Country:
                          </span>{" "}
                          <span className="font-semibold text-stone-800">
                            {selectedShopData?.country || "—"}
                          </span>
                        </div>
                        {/* City */}
                        <div>
                          <span className="font-medium text-stone-500">
                            City:
                          </span>{" "}
                          <span className="font-semibold text-stone-800">
                            {selectedShopData?.city || "—"}
                          </span>
                        </div>
                        {/* Project */}
                        <div>
                          <span className="font-medium text-stone-500">
                            Project:
                          </span>{" "}
                          <span className="font-semibold text-stone-800">
                            {selectedShopData?.projectNumber ?? "-"}
                          </span>
                        </div>
                      </div>

                      {/* Device actions */}
                      <div className="border-b border-stone-200 pb-3" />
                      <div className="border-b border-stone-200 pb-3">
                        <h3 className="text-base font-bold text-stone-900">
                          Device Actions
                        </h3>

                        <p className="mt-1 text-sm text-stone-500">
                          Quick access to device management.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPopup(true)}
                        className="flex h-12 items-center justify-center rounded-xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 shadow-sm hover:bg-stone-100 transition"
                      >
                        Device Details
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowSettingsPopup(true)}
                        className="flex h-12 items-center justify-center rounded-xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 shadow-sm hover:bg-stone-100 transition"
                      >
                        Device Settings
                      </button>
                    </div>

                    {/* Security events chart */}
                    <div className="lg:col-span-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                      <ShopLogsChart
                        title="Security Events"
                        intrusionLogs={shopLogs?.intrusionAlarms ?? []}
                        doorWarningLogs={shopLogs?.openDoorWarnings ?? []}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Display the door event logs returned by the Shop Logs API. */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
                        <h4 className="text-lg font-bold text-stone-800">
                          Door Open Warnings
                        </h4>

                        {/* Display the total number of Door Open Warning events returned by the Shop Logs API. */}

                        <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-bold rounded-full">
                          {shopLogs?.openDoorWarnings?.length ?? 0}
                        </span>
                      </div>

                      {/* Display all Door Open Warning events returned by the API. */}

                      <LogDisplay
                        logs={shopLogs?.openDoorWarnings ?? []}
                        title="Door Open Warnings"
                      />
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
                        <h4 className="text-lg font-bold text-stone-800">
                          Intrusion Alarms
                        </h4>

                        <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-bold rounded-full">
                          {shopLogs?.intrusionAlarms?.length ?? 0}
                        </span>
                      </div>

                      <LogDisplay
                        logs={shopLogs?.intrusionAlarms ?? []}
                        title="Intrusion Alarms"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center">
          <SimpleShopDetails
            isOpen={showPopup}
            shop={selectedShopData}
            onClose={() => setShowPopup(false)}
          />
        </div>
      )}

      {showSettingsPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center">
          <SimpleDeviceSettigns
            isOpen={showSettingsPopup}
            shop={selectedShopData}
            onClose={() => setShowSettingsPopup(false)}
          />
        </div>
      )}
      {/* Display the PDF preview before downloading the report. */}
      {showPdfPreview && (
        <PdfPreview
          shop={selectedShopData}
          shopLogs={shopLogs}
          controllers={shopControllers}
          onClose={() => setShowPdfPreview(false)}
        />
      )}
      {showMonthlyReportPreview && monthlyReportData && (
        <MonthlyReportPdfPreview
          shop={monthlyReportData.shop ?? selectedShopData}
          report={monthlyReportData}
          controllers={monthlyReportData.controllers ?? []}
          isLoading={isMonthlyReportLoading}
          onPeriodChange={({ year, month }) => {
            // Store the selected period.
            setReportYear(year);
            setReportMonth(month);
          }}
          onClose={() => setShowMonthlyReportPreview(false)}
        />
      )}
    </>
  );
};

export default ShopLogs;
